import { Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges, TemplateRef, ViewChild } from '@angular/core';
import { IUserMcp, UserMcpTransport } from '@platfio/interfaces/platfio';
import { Subscription } from 'rxjs';
import { UiPopoverService } from 'src/app/components/elements/ui-popover/ui-popover.service';
import { AuthService } from 'src/app/services/auth.service';
import { ThemeService } from 'src/app/services/theme.service';
import { ToastService } from 'src/app/services/toast.service';
import { UserMcpService, UserMcpTestResult } from 'src/app/services/user-mcp.service';

interface ConnectorHeaderEntry {
  id: string;
  key: string;
  value: string;
}

interface ConnectorDraft {
  mcpId: string;
  name: string;
  note: string;
  serverUrl: string;
  transport: UserMcpTransport;
  headerEntries: ConnectorHeaderEntry[];
  enabled: boolean;
  agencyShared: boolean;
}

@Component({
  selector: 'app-chat-connectors-modal',
  template: `
    <div class="chat-connectors-modal" [class.chat-connectors-modal--profile]="variant === 'profile'">
      <div class="chat-connectors-modal__grid">
        <section class="chat-connectors-modal__panel">
          <div class="chat-connectors-modal__panel-heading">
            <div>
              <h4 class="chat-connectors-modal__panel-title">Configured MCP servers</h4>
            </div>

            <div class="chat-connectors-modal__panel-heading-actions">
              <ui-badge variant="subtle" color="gray" size="sm">{{ connectors.length }}</ui-badge>

              <ui-button variant="outline"
                         color="primary"
                         size="sm"
                         icon="fa-regular fa-plus"
                         [disabled]="!canCreate"
                         (buttonClick)="startCreateConnector($event)">
                Add Connector
              </ui-button>
            </div>
          </div>

          <div class="chat-connectors-modal__state" *ngIf="connectorsLoading">Loading connectors...</div>
          <div class="chat-connectors-modal__state chat-connectors-modal__state--error" *ngIf="!connectorsLoading && connectorsError">{{ connectorsError }}</div>
          <div class="chat-connectors-modal__state" *ngIf="!connectorsLoading && !connectorsError && connectors.length === 0">
            {{ connectorsEmptyMessage }}
          </div>

          <div class="chat-connectors-modal__connector-list" *ngIf="!connectorsLoading && !connectorsError && connectors.length > 0">
            <ui-card class="chat-connectors-modal__connector-card"
                     *ngFor="let connector of connectors; trackBy: trackConnectorById"
                     variant="default"
                     size="sm"
                     layout="item"
                     [surface]="selectedMcpId === connector.mcpId ? 'soft' : 'default'"
                     [accent]="selectedMcpId === connector.mcpId ? 'blue' : 'none'"
                     [clickable]="true"
                     (cardClick)="selectConnector(connector)">
              <ng-template #cardTitle>{{ connector.name }}</ng-template>

              <ng-template #cardSubtitle>
                <span class="chat-connectors-modal__connector-url">{{ connector.serverUrl }}</span>
              </ng-template>

              <ng-template #cardSupporting>
                <div class="chat-connectors-modal__card-supporting">
                  <div class="chat-connectors-modal__badge-row">
                    <ui-badge variant="subtle" color="info" size="xs">{{ getTransportLabel(connector.transport) }}</ui-badge>
                    <ui-badge variant="subtle"
                              [color]="connector.enabled ? 'success' : 'gray'"
                              size="xs">
                      {{ connector.enabled ? 'Enabled' : 'Disabled' }}
                    </ui-badge>
                    <ui-badge variant="outline" color="gray" size="xs">{{ getConnectorVisibilityLabel(connector) }}</ui-badge>
                    <ui-badge variant="outline" color="gray" size="xs">{{ formatHeadersPreview(connector.headers) }}</ui-badge>
                  </div>

                  <div class="chat-connectors-modal__connector-description" *ngIf="connector.note || connector.description">
                    {{ connector.note || connector.description }}
                  </div>
                </div>
              </ng-template>

              <ng-template #cardTrailing>
                <i class="fa-regular fa-chevron-right chat-connectors-modal__card-chevron"></i>
              </ng-template>
            </ui-card>
          </div>
        </section>

        <section class="chat-connectors-modal__panel chat-connectors-modal__panel--detail">
          <ng-container *ngIf="selectedConnector; else noConnectorSelectedState">
            <div class="chat-connectors-modal__detail-header">
              <div>
                <span class="chat-connectors-modal__eyebrow">Connector details</span>
                <h4 class="chat-connectors-modal__panel-title">{{ selectedConnector.name || 'Selected connector' }}</h4>
                <p class="chat-connectors-modal__hint">Edit, test, or remove this connector.</p>
              </div>

              <ui-badge variant="subtle"
                        [color]="selectedConnector.enabled ? 'success' : 'gray'"
                        size="sm">
                {{ selectedConnector.enabled ? 'Enabled' : 'Disabled' }}
              </ui-badge>
            </div>

            <div class="chat-connectors-modal__summary-grid">
              <ui-card class="chat-connectors-modal__summary-card"
                       variant="default"
                       size="sm"
                       surface="soft">
                <div class="chat-connectors-modal__summary-card-content">
                  <span class="chat-connectors-modal__label">Transport type</span>
                  <div class="chat-connectors-modal__summary-value">{{ getTransportLabel(selectedConnector.transport) }}</div>
                </div>
              </ui-card>

              <ui-card class="chat-connectors-modal__summary-card"
                       variant="default"
                       size="sm"
                       surface="soft">
                <div class="chat-connectors-modal__summary-card-content">
                  <span class="chat-connectors-modal__label">Custom headers</span>
                  <div class="chat-connectors-modal__summary-value">{{ formatHeadersPreview(selectedConnector.headers) }}</div>
                </div>
              </ui-card>

              <ui-card class="chat-connectors-modal__summary-card"
                       variant="default"
                       size="sm"
                       surface="soft">
                <div class="chat-connectors-modal__summary-card-content">
                  <span class="chat-connectors-modal__label">Access</span>
                  <div class="chat-connectors-modal__summary-value">{{ getConnectorVisibilityLabel(selectedConnector) }}</div>
                </div>
              </ui-card>

              <ui-card class="chat-connectors-modal__summary-card chat-connectors-modal__summary-card--full"
                       variant="default"
                       size="sm"
                       surface="soft">
                <div class="chat-connectors-modal__summary-card-content">
                  <span class="chat-connectors-modal__label">Server URL</span>
                  <div class="chat-connectors-modal__summary-value chat-connectors-modal__summary-value--mono">{{ selectedConnector.serverUrl }}</div>
                </div>
              </ui-card>

              <ui-card class="chat-connectors-modal__summary-card chat-connectors-modal__summary-card--full"
                       variant="default"
                       size="sm"
                       surface="soft">
                <div class="chat-connectors-modal__summary-card-content">
                  <span class="chat-connectors-modal__label">Usage note</span>
                  <div class="chat-connectors-modal__summary-value">{{ selectedConnector.note || selectedConnector.description || 'No usage note configured.' }}</div>
                </div>
              </ui-card>
            </div>

            <div class="chat-connectors-modal__actions">
              <div class="chat-connectors-modal__summary-actions">
                <ui-button variant="outline"
                           color="primary"
                           size="sm"
                           icon="fa-regular fa-pen-to-square"
                           *ngIf="canManageSelectedConnector"
                           [disabled]="savingConnector || deletingConnector"
                           (buttonClick)="openEditConnector($event)">
                  Edit Connector
                </ui-button>
              </div>
            </div>
          </ng-container>

          <ng-template #noConnectorSelectedState>
            <div class="chat-connectors-modal__state">Select a connector to review it, or add a new endpoint.</div>
          </ng-template>
        </section>
      </div>

      <ng-template #createConnectorPopoverTemplate>
        <div class="chat-connectors-modal__popover">
          <div class="chat-connectors-modal__form-grid">
            <div class="chat-connectors-modal__field-shell">
              <ui-form-field label="Server name"
                             type="text"
                             placeholder="e.g., Linear MCP"
                             [(ngModel)]="popoverDraft.name"
                             (ngModelChange)="clearCreateTestState()"
                             [disabled]="!canCreate || savingConnector || testingConnector">
              </ui-form-field>
            </div>

            <div class="chat-connectors-modal__field-shell">
              <ui-form-field label="Transport type"
                             type="select"
                             [(ngModel)]="popoverDraft.transport"
                             (ngModelChange)="clearCreateTestState()"
                             [options]="transportOptions"
                             [disabled]="!canCreate || savingConnector || testingConnector">
              </ui-form-field>
            </div>

            <div class="chat-connectors-modal__field-shell chat-connectors-modal__field-shell--full">
              <ui-form-field label="Note"
                             type="textarea"
                             placeholder="Describe how and when the agent should use this MCP."
                             [rows]="4"
                             [(ngModel)]="popoverDraft.note"
                             (ngModelChange)="clearCreateTestState()"
                             [disabled]="!canCreate || savingConnector || testingConnector">
              </ui-form-field>
            </div>

            <div class="chat-connectors-modal__field-shell chat-connectors-modal__field-shell--full">
              <ui-form-field label="Server URL"
                             type="url"
                             placeholder="https://example.com/mcp"
                             [(ngModel)]="popoverDraft.serverUrl"
                             (ngModelChange)="clearCreateTestState()"
                             [disabled]="!canCreate || savingConnector || testingConnector">
              </ui-form-field>
            </div>
          </div>

          <div class="chat-connectors-modal__headers">
            <ui-form-field type="action"
                           label="Custom Headers"
                           placeholder="Add Header"
                           icon="fa-regular fa-plus"
                           [showActionChevron]="false"
                           [disabled]="!canCreate || savingConnector || testingConnector"
                           (actionClick)="addHeaderRow(isEditingConnector ? 'selected' : 'create')">
            </ui-form-field>

            <div class="chat-connectors-modal__header-list" *ngIf="popoverDraft.headerEntries.length > 0">
              <div class="chat-connectors-modal__header-row"
                   *ngFor="let header of popoverDraft.headerEntries; trackBy: trackHeaderById">
                <div class="chat-connectors-modal__field-shell">
                  <ui-form-field label="Header name"
                                 type="text"
                                 placeholder="Authorization"
                                 [(ngModel)]="header.key"
                                 (ngModelChange)="clearCreateTestState()"
                                 [disabled]="!canCreate || savingConnector || testingConnector">
                  </ui-form-field>
                </div>

                <div class="chat-connectors-modal__field-shell">
                  <ui-form-field label="Header value"
                                 type="text"
                                 placeholder="Bearer ..."
                                 [(ngModel)]="header.value"
                                 (ngModelChange)="clearCreateTestState()"
                                 [disabled]="!canCreate || savingConnector || testingConnector">
                  </ui-form-field>
                </div>

                <button class="chat-connectors-modal__icon-button"
                        type="button"
                  [disabled]="!canCreate || savingConnector || testingConnector"
                        (click)="removeHeaderRow(header.id, isEditingConnector ? 'selected' : 'create')"
                        aria-label="Remove custom header">
                  <i class="fa-regular fa-trash-can"></i>
                </button>
              </div>
            </div>
          </div>

          <div class="chat-connectors-modal__field-shell chat-connectors-modal__field-shell--full">
            <ui-form-field type="toggle"
                           label="Expose this connector to the agent"
                           toggleLabel="Enabled"
                           toggleLabelOff="Disabled"
                           [(ngModel)]="popoverDraft.enabled"
                           [disabled]="!canCreate || savingConnector || testingConnector">
            </ui-form-field>
          </div>

          <div class="chat-connectors-modal__field-shell chat-connectors-modal__field-shell--full">
            <ui-form-field type="toggle"
                           label="Share this connector with everyone in the agency"
                           toggleLabel="Everyone"
                           toggleLabelOff="Only me"
                           [(ngModel)]="popoverDraft.agencyShared"
                           [disabled]="!canCreate || savingConnector || testingConnector">
            </ui-form-field>
          </div>

          <div class="chat-connectors-modal__test-result"
               *ngIf="editorTestResult || editorTestError"
               [class.chat-connectors-modal__test-result--error]="!!editorTestError">
            <div class="chat-connectors-modal__test-result-title">
              {{ editorTestError || editorTestResult?.message }}
            </div>

            <div class="chat-connectors-modal__test-result-meta" *ngIf="editorTestResult">
              <span *ngIf="editorTestResult.serverName">
                {{ editorTestResult.serverName }}<ng-container *ngIf="editorTestResult.serverVersion"> v{{ editorTestResult.serverVersion }}</ng-container>
              </span>
              <span>Protocol {{ editorTestResult.protocolVersion }}</span>
              <span>{{ editorTestResult.toolCount }} tool{{ editorTestResult.toolCount === 1 ? '' : 's' }}</span>
            </div>

            <div class="chat-connectors-modal__test-result-tools"
                 *ngIf="editorTestResult && editorTestResult.toolNames.length > 0">
              {{ editorTestResult.toolNames.join(', ') }}
            </div>
          </div>

          <div class="chat-connectors-modal__popover-actions">
            <div class="chat-connectors-modal__actions-primary">
              <ui-button variant="outline"
                         color="gray"
                         size="sm"
                         icon="fa-regular fa-vial-circle-check"
                         [disabled]="!canCreate || savingConnector || testingConnector || !popoverDraft.serverUrl.trim()"
                         (buttonClick)="testEditorDraft()">
                {{ testingConnector ? 'Testing...' : 'Test Connection' }}
              </ui-button>

              <ui-button variant="outline"
                         color="primary"
                         size="sm"
                         icon="fa-regular fa-floppy-disk"
                         [disabled]="!canCreate || savingConnector || testingConnector || !popoverDraft.name.trim() || !popoverDraft.serverUrl.trim()"
                         (buttonClick)="saveEditorDraft()">
                {{ savingConnector ? (isEditingConnector ? 'Saving...' : 'Creating...') : (isEditingConnector ? 'Save Changes' : 'Create Connector') }}
              </ui-button>
            </div>

            <div class="chat-connectors-modal__summary-actions">
              <ui-button variant="outline"
                         color="gray"
                         size="sm"
                         icon="fa-regular fa-xmark"
                         [disabled]="savingConnector || testingConnector"
                         (buttonClick)="discardCreateDraft()">
                {{ isEditingConnector ? 'Discard Changes' : 'Discard Draft' }}
              </ui-button>

              <ui-button variant="outline"
                         color="danger"
                         size="sm"
                         icon="fa-regular fa-trash-can"
                         *ngIf="isEditingConnector && canManageSelectedConnector && selectedConnector"
                         [disabled]="savingConnector || deletingConnector || testingConnector"
                         (buttonClick)="deleteSelectedConnector()">
                {{ deletingConnector ? 'Deleting...' : 'Delete Connector' }}
              </ui-button>
            </div>
          </div>
        </div>
      </ng-template>
    </div>
  `,
  styles: [`
    :host {
      display: block;

      --chat-connectors-panel-bg: rgba(16, 19, 24, 0.96);
      --chat-connectors-panel-border: rgba(255, 255, 255, 0.08);
      --chat-connectors-panel-muted: rgba(255, 255, 255, 0.52);
      --chat-connectors-panel-subtle: rgba(255, 255, 255, 0.34);
      --chat-connectors-panel-strong: rgba(248, 250, 252, 0.95);
      --chat-connectors-panel-card: rgba(255, 255, 255, 0.03);
      --chat-connectors-panel-card-hover: rgba(255, 255, 255, 0.06);
      --chat-connectors-panel-card-active: rgba(59, 130, 246, 0.12);
      --chat-connectors-panel-card-active-border: rgba(96, 165, 250, 0.4);
      --chat-connectors-panel-error: #fca5a5;
      --chat-connectors-panel-enabled: #86efac;
      --chat-connectors-panel-input-bg: rgba(255, 255, 255, 0.04);
      --chat-connectors-panel-danger: #f87171;
    }

    :host.theme-light {
      --chat-connectors-panel-bg: #ffffff;
      --chat-connectors-panel-border: rgba(15, 23, 42, 0.09);
      --chat-connectors-panel-muted: rgba(51, 65, 85, 0.72);
      --chat-connectors-panel-subtle: rgba(71, 85, 105, 0.55);
      --chat-connectors-panel-strong: #0f172a;
      --chat-connectors-panel-card: rgba(148, 163, 184, 0.08);
      --chat-connectors-panel-card-hover: rgba(148, 163, 184, 0.13);
      --chat-connectors-panel-card-active: rgba(59, 130, 246, 0.08);
      --chat-connectors-panel-card-active-border: rgba(59, 130, 246, 0.26);
      --chat-connectors-panel-error: #dc2626;
      --chat-connectors-panel-enabled: #15803d;
      --chat-connectors-panel-input-bg: #ffffff;
      --chat-connectors-panel-danger: #dc2626;
    }

    .chat-connectors-modal {
      display: flex;
      flex-direction: column;
      gap: 14px;
      color: var(--chat-connectors-panel-strong);
    }

    .chat-connectors-modal__popover {
      display: flex;
      flex-direction: column;
      gap: 16px;
      min-width: 0;
    }

    .chat-connectors-modal__grid {
      display: grid;
      grid-template-columns: minmax(280px, 0.92fr) minmax(480px, 1.28fr);
      gap: 14px;
      align-items: start;
    }

    .chat-connectors-modal__panel {
      min-width: 0;
      border: 1px solid var(--chat-connectors-panel-border);
      border-radius: 18px;
      background: var(--chat-connectors-panel-bg);
      padding: 18px;
      display: flex;
      flex-direction: column;
      gap: 16px;
    }

    .chat-connectors-modal__panel-heading,
    .chat-connectors-modal__panel-heading-actions,
    .chat-connectors-modal__connector-top,
    .chat-connectors-modal__detail-header,
    .chat-connectors-modal__actions,
    .chat-connectors-modal__actions-primary,
    .chat-connectors-modal__toggle-row {
      display: flex;
      justify-content: space-between;
      gap: 12px;
    }

    .chat-connectors-modal__panel-heading-actions,
    .chat-connectors-modal__actions,
    .chat-connectors-modal__actions-primary,
    .chat-connectors-modal__toggle-row {
      align-items: center;
    }

    .chat-connectors-modal__eyebrow,
    .chat-connectors-modal__label {
      display: block;
      color: var(--chat-connectors-panel-subtle);
      font-size: 11px;
      font-weight: 700;
      letter-spacing: 0.08em;
      text-transform: uppercase;
    }

    .chat-connectors-modal__panel-title {
      margin: 0;
      color: var(--chat-connectors-panel-strong);
      font-size: 16px;
      font-weight: 600;
      line-height: 1.3;
    }

    .chat-connectors-modal__eyebrow + .chat-connectors-modal__panel-title {
      margin-top: 4px;
    }

    .chat-connectors-modal__icon-button:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }

    .chat-connectors-modal__hint,
    .chat-connectors-modal__state,
    .chat-connectors-modal__connector-description,
    .chat-connectors-modal__toggle-row {
      margin: 0;
      color: var(--chat-connectors-panel-muted);
      font-size: 12px;
      line-height: 1.55;
    }

    .chat-connectors-modal__connector-list,
    .chat-connectors-modal__header-list {
      display: flex;
      flex-direction: column;
      gap: 10px;
    }

    .chat-connectors-modal__connector-list ui-card,
    .chat-connectors-modal__summary-grid ui-card {
      display: block;
      min-width: 0;
    }

    .chat-connectors-modal__test-result-tools {
      color: var(--chat-connectors-panel-muted);
      font-size: 12px;
      line-height: 1.55;
    }

    .chat-connectors-modal__summary-grid {
      display: grid;
      grid-template-columns: repeat(2, minmax(0, 1fr));
      gap: 12px;
    }

    .chat-connectors-modal__summary-card {
      min-width: 0;
    }

    .chat-connectors-modal__summary-card--full {
      grid-column: 1 / -1;
    }

    .chat-connectors-modal__summary-card-content {
      display: flex;
      flex-direction: column;
      gap: 8px;
      min-width: 0;
    }

    .chat-connectors-modal__summary-value {
      color: var(--chat-connectors-panel-strong);
      font-size: 13px;
      line-height: 1.6;
      word-break: break-word;
    }

    .chat-connectors-modal__summary-value--mono {
      font-family: 'SF Mono', Monaco, 'Cascadia Code', monospace;
      font-size: 12px;
    }

    .chat-connectors-modal__summary-actions {
      display: inline-flex;
      align-items: center;
      flex-wrap: wrap;
      gap: 10px;
    }

    .chat-connectors-modal__connector-card {
      width: 100%;
    }

    .chat-connectors-modal__card-supporting {
      display: flex;
      flex-direction: column;
      gap: 10px;
      min-width: 0;
    }

    .chat-connectors-modal__badge-row {
      display: flex;
      flex-wrap: wrap;
      gap: 8px;
    }

    .chat-connectors-modal__card-chevron {
      color: var(--chat-connectors-panel-subtle);
      font-size: 13px;
    }

    .chat-connectors-modal__state {
      padding: 14px 16px;
      border-radius: 14px;
      border: 1px solid var(--chat-connectors-panel-border);
      background: var(--chat-connectors-panel-card);
    }

    .chat-connectors-modal__connector-url {
      color: var(--chat-connectors-panel-strong);
      font-family: 'SF Mono', Monaco, 'Cascadia Code', monospace;
      font-size: 12px;
      line-height: 1.5;
      word-break: break-all;
    }

    .chat-connectors-modal__form-grid {
      display: grid;
      grid-template-columns: repeat(2, minmax(0, 1fr));
      gap: 12px;
    }

    .chat-connectors-modal__field,
    .chat-connectors-modal__headers {
      min-width: 0;
      display: flex;
      flex-direction: column;
      gap: 8px;
    }

    .chat-connectors-modal__field-shell {
      min-width: 0;
    }

    .chat-connectors-modal__field-shell--full {
      grid-column: 1 / -1;
    }

    .chat-connectors-modal__field--full,
    .chat-connectors-modal__headers {
      grid-column: 1 / -1;
    }

    .chat-connectors-modal__text-input,
    .chat-connectors-modal__textarea,
    .chat-connectors-modal__select {
      width: 100%;
      border-radius: 12px;
      border: 1px solid var(--chat-connectors-panel-border);
      background: var(--chat-connectors-panel-input-bg);
      color: var(--chat-connectors-panel-strong);
      font-size: 14px;
    }

    .chat-connectors-modal__text-input,
    .chat-connectors-modal__select {
      min-height: 44px;
      padding: 0 14px;
    }

    .chat-connectors-modal__textarea {
      padding: 12px 14px;
      resize: vertical;
      min-height: 112px;
    }

    .chat-connectors-modal__text-input::placeholder,
    .chat-connectors-modal__textarea::placeholder {
      color: var(--chat-connectors-panel-subtle);
    }

    .chat-connectors-modal__text-input:focus,
    .chat-connectors-modal__textarea:focus,
    .chat-connectors-modal__select:focus {
      outline: none;
      border-color: var(--chat-connectors-panel-card-active-border);
      box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.14);
    }

    .chat-connectors-modal__header-row {
      display: grid;
      grid-template-columns: minmax(0, 1fr) minmax(0, 1fr) auto;
      gap: 10px;
      align-items: center;
    }

    .chat-connectors-modal__header-row > * {
      min-width: 0;
    }

    .chat-connectors-modal__icon-button {
      width: 40px;
      height: 40px;
      border-radius: 12px;
      border: 1px solid rgba(248, 113, 113, 0.2);
      background: rgba(248, 113, 113, 0.08);
      color: var(--chat-connectors-panel-danger);
      display: inline-flex;
      align-items: center;
      justify-content: center;
    }

    .chat-connectors-modal__test-result {
      border-radius: 14px;
      border: 1px solid rgba(34, 197, 94, 0.18);
      background: rgba(34, 197, 94, 0.08);
      padding: 12px 14px;
      display: flex;
      flex-direction: column;
      gap: 8px;
    }

    .chat-connectors-modal__test-result--error {
      border-color: rgba(248, 113, 113, 0.2);
      background: rgba(248, 113, 113, 0.08);
    }

    .chat-connectors-modal__test-result-title,
    .chat-connectors-modal__test-result-meta {
      color: var(--chat-connectors-panel-strong);
      font-size: 12px;
      line-height: 1.55;
    }

    .chat-connectors-modal__test-result-meta {
      display: flex;
      flex-wrap: wrap;
      gap: 8px;
      color: var(--chat-connectors-panel-muted);
    }

    .chat-connectors-modal__popover-actions {
      display: flex;
      justify-content: space-between;
      gap: 12px;
      align-items: center;
    }

    .chat-connectors-modal__state--error {
      color: var(--chat-connectors-panel-error);
    }

    @media (max-width: 960px) {
      .chat-connectors-modal__grid,
      .chat-connectors-modal__form-grid,
      .chat-connectors-modal__summary-grid {
        grid-template-columns: minmax(0, 1fr);
      }

      .chat-connectors-modal__header-row {
        grid-template-columns: minmax(0, 1fr);
      }

      .chat-connectors-modal__panel-heading,
      .chat-connectors-modal__detail-header,
      .chat-connectors-modal__actions,
      .chat-connectors-modal__popover-actions {
        flex-direction: column;
        align-items: flex-start;
      }
    }
  `],
  host: { '[class.theme-light]': 'theme.isLight' }
})
export class ChatConnectorsModalComponent implements OnInit, OnChanges, OnDestroy {
  @Input() uid = '';
  @Input() canEdit = true;
  @Input() variant: 'chat' | 'profile' = 'chat';
  @ViewChild('createConnectorPopoverTemplate') createConnectorPopoverTemplate?: TemplateRef<unknown>;

  connectors: IUserMcp[] = [];
  connectorsLoading = true;
  connectorsError = '';
  isCreatePopoverOpen = false;
  editorMode: 'create' | 'edit' | null = null;
  selectedMcpId = '';
  savingConnector = false;
  deletingConnector = false;
  draft: ConnectorDraft = this.createEmptyDraft();
  createDraft: ConnectorDraft = this.createEmptyDraft();
  testingConnector = false;
  editorTestResult: UserMcpTestResult | null = null;
  editorTestError = '';
  readonly transportOptions = [{ value: 'streamable-http', label: 'HTTP' }];

  private authSubscription?: Subscription;
  private connectorsSubscription?: Subscription;
  private authUid = '';
  private activeUid = '';
  private suppressCreatePopoverReset = false;

  constructor(
    private authService: AuthService,
    private userMcpService: UserMcpService,
    private popoverService: UiPopoverService,
    private toastService: ToastService,
    public theme: ThemeService
  ) {}

  ngOnInit(): void {
    this.authSubscription = this.authService.afAuth.authState.subscribe((user) => {
      this.authUid = user?.uid || '';
      this.syncActiveUid();
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['uid']) {
      this.syncActiveUid();
    }
  }

  ngOnDestroy(): void {
    this.authSubscription?.unsubscribe();
    this.connectorsSubscription?.unsubscribe();
  }

  get canCreate(): boolean {
    return this.canEdit && !!this.activeUid;
  }

  get canManageSelectedConnector(): boolean {
    return this.canManageConnector(this.selectedConnector);
  }

  get selectedConnector(): IUserMcp | null {
    return this.connectors.find((connector) => connector.mcpId === this.selectedMcpId) || null;
  }

  get isEditingConnector(): boolean {
    return this.editorMode === 'edit';
  }

  get popoverDraft(): ConnectorDraft {
    return this.isEditingConnector ? this.draft : this.createDraft;
  }

  get connectorsEmptyMessage(): string {
    return this.activeUid
      ? 'No connectors are configured for this account yet.'
      : 'Sign in to configure connectors.';
  }

  trackConnectorById(index: number, connector: IUserMcp): string {
    void index;
    return connector.mcpId;
  }

  trackHeaderById(index: number, header: ConnectorHeaderEntry): string {
    void index;
    return header.id;
  }

  startCreateConnector(event: Event): void {
    if (!this.canCreate || !this.createConnectorPopoverTemplate) {
      return;
    }

    const trigger = event.currentTarget as HTMLElement | null;
    if (!trigger) {
      return;
    }

    this.openConnectorEditor(trigger, 'create');
  }

  selectConnector(connector: IUserMcp): void {
    this.selectedMcpId = connector.mcpId;
    this.draft = this.toDraft(connector);
  }

  addHeaderRow(target: 'selected' | 'create'): void {
    const nextDraft = target === 'create' ? this.createDraft : this.draft;
    nextDraft.headerEntries = [...nextDraft.headerEntries, this.createHeaderEntry()];
    if (target === 'create') {
      this.clearCreateTestState();
    }
  }

  removeHeaderRow(headerId: string, target: 'selected' | 'create'): void {
    const nextDraft = target === 'create' ? this.createDraft : this.draft;
    nextDraft.headerEntries = nextDraft.headerEntries.filter((header) => header.id !== headerId);
    if (target === 'create') {
      this.clearCreateTestState();
    }
  }

  openEditConnector(event: Event): void {
    const connector = this.selectedConnector;
    const trigger = event.currentTarget as HTMLElement | null;
    if (!connector || !trigger || !this.canManageConnector(connector)) {
      return;
    }

    this.openConnectorEditor(trigger, 'edit', connector);
  }

  async saveDraft(): Promise<void> {
    if (!this.activeUid) {
      this.toastService.showError('Sign in before saving a connector.');
      return;
    }

    const name = this.draft.name.trim();
    const serverUrl = this.draft.serverUrl.trim();
    if (!name || !serverUrl) {
      this.toastService.showError('Connector name and URL are required.');
      return;
    }

    this.savingConnector = true;

    try {
      await this.userMcpService.upsertMcp(this.activeUid, {
        mcpId: this.selectedMcpId,
        name,
        note: this.draft.note,
        serverUrl,
        transport: this.draft.transport,
        headers: this.serializeHeaders(this.draft.headerEntries),
        enabled: this.draft.enabled,
        agencyId: this.getActiveAgencyId(),
        agencyShared: this.draft.agencyShared
      });

      this.toastService.showSuccess('Connector saved.');
    } catch (error) {
      console.error('Error saving connector:', error);
      this.toastService.showError((error as Error).message || 'Unable to save this connector right now.');
    } finally {
      this.savingConnector = false;
    }
  }

  async saveNewConnector(): Promise<void> {
    if (!this.activeUid) {
      this.toastService.showError('Sign in before saving a connector.');
      return;
    }

    const name = this.createDraft.name.trim();
    const serverUrl = this.createDraft.serverUrl.trim();
    if (!name || !serverUrl) {
      this.toastService.showError('Connector name and URL are required.');
      return;
    }

    this.savingConnector = true;
    const mcpId = this.createMcpId();

    try {
      await this.userMcpService.upsertMcp(this.activeUid, {
        mcpId,
        name,
        note: this.createDraft.note,
        serverUrl,
        transport: this.createDraft.transport,
        headers: this.serializeHeaders(this.createDraft.headerEntries),
        enabled: this.createDraft.enabled,
        agencyId: this.getActiveAgencyId(),
        agencyShared: this.createDraft.agencyShared
      });

      this.selectedMcpId = mcpId;
      this.draft = this.cloneDraft({
        ...this.createDraft,
        mcpId
      });
      this.suppressCreatePopoverReset = false;
      this.closeCreatePopover();
      this.toastService.showSuccess('Connector created.');
    } catch (error) {
      console.error('Error creating connector:', error);
      this.toastService.showError((error as Error).message || 'Unable to create this connector right now.');
    } finally {
      this.savingConnector = false;
    }
  }

  async testEditorDraft(): Promise<void> {
    const serverUrl = this.popoverDraft.serverUrl.trim();
    if (!serverUrl) {
      this.editorTestError = 'Enter a server URL before testing.';
      this.editorTestResult = null;
      return;
    }

    this.testingConnector = true;
    this.clearCreateTestState();

    try {
      this.editorTestResult = await this.userMcpService.testMcp({
        serverUrl,
        transport: this.popoverDraft.transport,
        headers: this.serializeHeaders(this.popoverDraft.headerEntries)
      });
    } catch (error) {
      this.editorTestResult = null;
      this.editorTestError = (error as Error).message || 'Unable to test this connector right now.';
    } finally {
      this.testingConnector = false;
    }
  }

  async saveEditorDraft(): Promise<void> {
    if (this.isEditingConnector) {
      await this.saveDraft();
      return;
    }

    await this.saveNewConnector();
  }

  discardCreateDraft(): void {
    this.closeCreatePopover();
  }

  clearCreateTestState(): void {
    this.editorTestResult = null;
    this.editorTestError = '';
  }

  resetDraft(): void {
    if (this.selectedConnector) {
      this.draft = this.toDraft(this.selectedConnector);
    }
  }

  async deleteSelectedConnector(): Promise<void> {
    const connector = this.selectedConnector;
    if (!this.activeUid || !connector || this.deletingConnector || !this.canManageConnector(connector)) {
      return;
    }

    if (typeof window !== 'undefined' && !window.confirm(`Delete ${connector.name}?`)) {
      return;
    }

    this.deletingConnector = true;

    try {
      await this.userMcpService.deleteMcp(this.activeUid, connector.mcpId);
      this.closeCreatePopover();
      this.selectedMcpId = '';
      this.draft = this.createEmptyDraft();
      this.toastService.showSuccess('Connector deleted.');
    } catch (error) {
      console.error('Error deleting connector:', error);
      this.toastService.showError('Unable to delete this connector right now.');
    } finally {
      this.deletingConnector = false;
    }
  }

  formatHeadersPreview(headers: Record<string, string> | null | undefined): string {
    if (!headers || Object.keys(headers).length === 0) {
      return 'No custom headers';
    }

    return `${Object.keys(headers).length} custom ${Object.keys(headers).length === 1 ? 'header' : 'headers'}`;
  }

  getTransportLabel(transport: UserMcpTransport | null | undefined): string {
    return transport === 'streamable-http' ? 'HTTP' : 'HTTP';
  }

  getConnectorVisibilityLabel(connector: IUserMcp | null | undefined): string {
    return connector?.agencyShared ? 'Everyone in agency' : 'Only me';
  }

  private syncActiveUid(): void {
    const nextUid = this.uid || this.authUid || '';
    if (this.activeUid === nextUid) {
      return;
    }

    this.activeUid = nextUid;
    this.subscribeToMcps();
  }

  private subscribeToMcps(): void {
    this.connectorsSubscription?.unsubscribe();

    if (!this.activeUid) {
      this.connectors = [];
      this.connectorsLoading = false;
      this.connectorsError = '';
      this.selectedMcpId = '';
      this.draft = this.createEmptyDraft();
      this.createDraft = this.createEmptyDraft();
      this.clearCreateTestState();
      return;
    }

    this.connectorsLoading = true;
    this.connectorsError = '';
    this.connectors = [];

    const connectors$ = this.activeUid === this.authUid
      ? this.userMcpService.getAccessibleMcps(this.activeUid, this.getActiveAgencyId())
      : this.userMcpService.getMcps(this.activeUid);

    this.connectorsSubscription = connectors$.subscribe({
      next: (connectors) => {
        this.connectors = connectors || [];
        this.connectorsLoading = false;

        if (!this.connectors.length) {
          this.selectedMcpId = '';
          this.draft = this.createEmptyDraft();
          return;
        }

        const selectedStillExists = this.connectors.some((connector) => connector.mcpId === this.selectedMcpId);
        const nextSelected = selectedStillExists
          ? this.connectors.find((connector) => connector.mcpId === this.selectedMcpId) || this.connectors[0]
          : this.connectors[0];

        this.selectedMcpId = nextSelected.mcpId;

        if (!this.savingConnector) {
          this.draft = this.toDraft(nextSelected);
        }
      },
      error: (error) => {
        console.error('Error loading connectors:', error);
        this.connectors = [];
        this.connectorsLoading = false;
        this.connectorsError = 'Unable to load connectors right now.';
        this.selectedMcpId = '';
        this.draft = this.createEmptyDraft();
      }
    });
  }

  private toDraft(connector: IUserMcp): ConnectorDraft {
    return {
      mcpId: connector.mcpId,
      name: connector.name || '',
      note: connector.note || connector.description || '',
      serverUrl: connector.serverUrl || '',
      transport: connector.transport || 'streamable-http',
      headerEntries: this.toHeaderEntries(connector.headers),
      enabled: connector.enabled !== false,
      agencyShared: connector.agencyShared === true
    };
  }

  private createEmptyDraft(): ConnectorDraft {
    return {
      mcpId: '',
      name: '',
      note: '',
      serverUrl: '',
      transport: 'streamable-http',
      headerEntries: [],
      enabled: true,
      agencyShared: false
    };
  }

  private toHeaderEntries(headers: Record<string, string> | null | undefined): ConnectorHeaderEntry[] {
    if (!headers || Object.keys(headers).length === 0) {
      return [];
    }

    return Object.entries(headers).map(([key, value]) => this.createHeaderEntry(key, value));
  }

  private cloneDraft(draft: ConnectorDraft): ConnectorDraft {
    return {
      mcpId: draft.mcpId,
      name: draft.name,
      note: draft.note,
      serverUrl: draft.serverUrl,
      transport: draft.transport,
      enabled: draft.enabled,
      agencyShared: draft.agencyShared,
      headerEntries: draft.headerEntries.map((header) => ({
        id: header.id,
        key: header.key,
        value: header.value
      }))
    };
  }

  private closeCreatePopover(): void {
    if (!this.isCreatePopoverOpen) {
      return;
    }

    this.popoverService.close();
  }

  private onCreatePopoverClosed(): void {
    this.isCreatePopoverOpen = false;
    this.editorMode = null;

    if (this.suppressCreatePopoverReset) {
      this.suppressCreatePopoverReset = false;
      return;
    }

    this.createDraft = this.createEmptyDraft();
    this.draft = this.selectedConnector ? this.toDraft(this.selectedConnector) : this.createEmptyDraft();
    this.clearCreateTestState();
  }

  private openConnectorEditor(trigger: HTMLElement, mode: 'create' | 'edit', connector?: IUserMcp): void {
    this.editorMode = mode;
    this.createDraft = this.createEmptyDraft();
    this.draft = connector ? this.toDraft(connector) : this.draft;
    this.clearCreateTestState();
    this.isCreatePopoverOpen = true;
    this.popoverService.open({
      element: trigger,
      placement: 'bottom-right',
      align: 'end',
      title: mode === 'edit' ? 'Edit Connector' : 'Add Connector',
      bodyTemplate: this.createConnectorPopoverTemplate,
      padding: 16,
      backdrop: true,
      backdropDismiss: true,
      displayMode: this.theme.isLight ? 'light' : 'dark',
      sizeMode: 'custom',
      dimensions: {
        width: 680,
        maxWidth: 'calc(100vw - 24px)'
      },
      onClose: () => this.onCreatePopoverClosed()
    });
  }

  private serializeHeaders(headerEntries: ConnectorHeaderEntry[]): Record<string, string> | null {
    const normalized = headerEntries.reduce<Record<string, string>>((accumulator, header) => {
      const normalizedKey = header.key.trim();
      const normalizedValue = header.value.trim();

      if (normalizedKey && normalizedValue) {
        accumulator[normalizedKey] = normalizedValue;
      }

      return accumulator;
    }, {});

    return Object.keys(normalized).length > 0 ? normalized : null;
  }

  private createHeaderEntry(key = '', value = ''): ConnectorHeaderEntry {
    return {
      id: this.createLocalId('header'),
      key,
      value
    };
  }

  private createMcpId(): string {
    return this.createLocalId('mcp');
  }

  private createLocalId(prefix: string): string {
    const uuid = typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(16).slice(2)}`;

    return `${prefix}-${uuid}`;
  }

  private canManageConnector(connector: IUserMcp | null | undefined): boolean {
    return this.canCreate && (!!connector ? (connector.userId || this.activeUid) === this.activeUid : true);
  }

  private getActiveAgencyId(): string {
    return this.authService.aid || this.activeUid;
  }
}